import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type EngagementSummary, type FollowUpEntry, type SegmentQuery, type Subscriber } from '../api';
import { formatIsraeliTimestamp, parseIsraeliDate } from '../lib/format';

type StatusFilter = 'all' | 'subscribed' | 'unsubscribed';
type SortMode =
  | 'last-joined-desc'
  | 'last-joined-asc'
  | 'name-asc'
  | 'name-desc'
  | 'email-asc'
  | 'tag-asc'
  | 'last-event-desc'
  | 'last-event-asc'
  | 'engaged-desc';
type SubscribersCachePayload = { subscribers: Subscriber[]; cachedAt: number };

const SUBSCRIBERS_CACHE_KEY = 'subscribers_cache_v1';
const SUBSCRIBERS_CACHE_MAX_AGE_MS = 5 * 60 * 1000;

export function SubscribersPage() {
  const [subs, setSubs] = useState<Subscriber[] | null>(null);
  const [engagement, setEngagement] = useState<Record<string, EngagementSummary>>({});
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortMode, setSortMode] = useState<SortMode>('last-joined-desc');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [duplicatesOnly, setDuplicatesOnly] = useState(false);
  const [busyEmail, setBusyEmail] = useState<string | null>(null);
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [bulkTagDraft, setBulkTagDraft] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [segmentName, setSegmentName] = useState('');
  const [segmentBusy, setSegmentBusy] = useState(false);
  const [segmentMessage, setSegmentMessage] = useState<string | null>(null);
  const [followUps, setFollowUps] = useState<Record<string, FollowUpEntry>>({});
  const [isStaleView, setIsStaleView] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Subscriber | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.deleteSubscriber(deleteTarget.email, deleteTarget.row);
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      setDeleteError((e as Error).message);
    } finally {
      setDeleteBusy(false);
    }
  }
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const cached = readSubscribersCache();
    if (cached) {
      setSubs(cached.subscribers);
      setLastSyncedAt(cached.cachedAt);
      setIsStaleView(Date.now() - cached.cachedAt > SUBSCRIBERS_CACHE_MAX_AGE_MS);
    }
    refresh(Boolean(cached));
    // Engagement is best-effort — empty map if D1 isn't wired yet.
    api.engagement()
      .then((r) => setEngagement(r.engagement))
      .catch(() => setEngagement({}));
    api.listFollowUps()
      .then((r) => setFollowUps(r.followUps))
      .catch(() => setFollowUps({}));
  }, []);

  function refresh(silentIfCached = false) {
    api.listSubscribers()
      .then((r) => {
        setSubs(r.subscribers);
        setError(null);
        setIsStaleView(false);
        setLastSyncedAt(Date.now());
        writeSubscribersCache(r.subscribers);
      })
      .catch((e) => {
        if (!silentIfCached) setError(e.message);
      });
  }

  async function toggleStatus(s: Subscriber) {
    const next = s.status === 'subscribed' ? 'unsubscribed' : 'subscribed';
    const verb = next === 'unsubscribed' ? 'unsubscribe' : 'resubscribe';
    if (!confirm(`Are you sure you want to ${verb} ${s.email}?`)) return;

    setBusyEmail(s.email);
    setSubs((prev) =>
      prev ? prev.map((x) => (x.email === s.email && x.row === s.row ? { ...x, status: next } : x)) : prev,
    );
    try {
      await api.setSubscriberStatus(s.email, next);
    } catch (e) {
      alert(`Failed: ${(e as Error).message}`);
      refresh();
    } finally {
      setBusyEmail(null);
    }
  }

  function toggleSelected(email: string) {
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  }

  async function applyBulkTag(mode: 'add' | 'remove') {
    const tag = bulkTagDraft.trim().toLowerCase();
    if (!tag || selectedEmails.size === 0 || !subs) return;
    setBulkBusy(true);
    const selected = subs.filter((s) => selectedEmails.has(s.email));
    const errors: string[] = [];
    for (const s of selected) {
      const nextTags = mode === 'add'
        ? Array.from(new Set([...s.tags, tag]))
        : s.tags.filter((t) => t.toLowerCase() !== tag);
      try {
        await api.setTags(s.email, nextTags);
      } catch {
        errors.push(s.email);
      }
    }
    await refresh();
    setBulkBusy(false);
    if (errors.length) alert(`Failed on ${errors.length} subscriber(s).`);
  }

  function exportCsv() {
    const escapeCell = (value: string) => {
      const dangerous = /^[=+\-@]/.test(value);
      const safe = dangerous ? `'${value}` : value;
      return `"${safe.replace(/"/g, '""')}"`;
    };
    const lines = [
      ['Name', 'Email', 'Source', 'Status', 'Score', 'LastEvent', 'FollowUpAt', 'FollowUpNote'].map(escapeCell).join(','),
    ];
    for (const s of filtered) {
      const eng = engagement[s.email];
      const follow = followUps[s.email.toLowerCase()];
      lines.push([
        s.name || '',
        s.email,
        s.source || '',
        s.status,
        eng?.score || '',
        eng?.lastOpenAt ? new Date(eng.lastOpenAt).toISOString() : '',
        follow?.at || '',
        follow?.note || '',
      ].map((x) => escapeCell(String(x))).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function saveCurrentViewAsSegment() {
    if (!segmentName.trim()) {
      setSegmentMessage('Segment name is required.');
      return;
    }
    const query: SegmentQuery = {};
    if (statusFilter !== 'all') query.status = statusFilter;
    if (sourceFilter !== 'all') query.sources = [sourceFilter];
    setSegmentBusy(true);
    setSegmentMessage(null);
    try {
      const r = await api.saveSegment({ name: segmentName.trim(), query });
      setSegmentMessage(`Segment saved (#${r.id}).`);
      setSegmentName('');
    } catch (e) {
      setSegmentMessage(`Save failed: ${(e as Error).message}`);
    } finally {
      setSegmentBusy(false);
    }
  }

  const counts = useMemo(() => {
    if (!subs) return { total: 0, active: 0, unsubscribed: 0, unique: 0 };
    const unique = new Set(subs.map((s) => s.email.toLowerCase())).size;
    const active = subs.filter((s) => s.status === 'subscribed').length;
    return { total: subs.length, active, unsubscribed: subs.length - active, unique };
  }, [subs]);

  const duplicateEmails = useMemo(() => {
    if (!subs) return new Set<string>();
    const countsByEmail = new Map<string, number>();
    for (const s of subs) {
      const key = s.email.toLowerCase();
      countsByEmail.set(key, (countsByEmail.get(key) ?? 0) + 1);
    }
    return new Set(Array.from(countsByEmail.entries()).filter(([, c]) => c > 1).map(([e]) => e));
  }, [subs]);

  const availableSources = useMemo(() => {
    if (!subs) return [];
    return Array.from(new Set(subs.map((s) => s.source).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  }, [subs]);

  const filtered = useMemo(() => {
    if (!subs) return [];
    const q = search.trim().toLowerCase();
    const base = subs.filter((s) => {
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      if (sourceFilter !== 'all' && s.source !== sourceFilter) return false;
      if (duplicatesOnly && !duplicateEmails.has(s.email.toLowerCase())) return false;
      if (!q) return true;
      return (
        s.email.toLowerCase().includes(q) ||
        s.name.toLowerCase().includes(q) ||
        (s.source || '').toLowerCase().includes(q)
      );
    });

    const parseDate = parseIsraeliDate;
    // Nulls always sort last regardless of direction.
    const byNumberNullsLast = (a: number | null, b: number | null, desc: boolean) => {
      if (a == null && b == null) return 0;
      if (a == null) return 1;
      if (b == null) return -1;
      return desc ? b - a : a - b;
    };

    return [...base].sort((a, b) => {
      switch (sortMode) {
        case 'last-joined-desc':
          return byNumberNullsLast(parseDate(a.date), parseDate(b.date), true);
        case 'last-joined-asc':
          return byNumberNullsLast(parseDate(a.date), parseDate(b.date), false);
        case 'name-asc':
          return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
        case 'name-desc':
          return (b.name || '').localeCompare(a.name || '', undefined, { sensitivity: 'base' });
        case 'email-asc':
          return (a.email || '').localeCompare(b.email || '', undefined, { sensitivity: 'base' });
        case 'tag-asc':
          return (a.source || '').localeCompare(b.source || '', undefined, { sensitivity: 'base' });
        case 'last-event-desc':
        case 'last-event-asc':
          return byNumberNullsLast(
            engagement[a.email]?.lastOpenAt ?? null,
            engagement[b.email]?.lastOpenAt ?? null,
            sortMode === 'last-event-desc',
          );
        case 'engaged-desc': {
          // Clicks are the stronger buying signal, so they weigh 3× an open.
          const scoreOf = (email: string) => {
            const e = engagement[email];
            if (!e) return null;
            return e.clicks * 3 + e.opens;
          };
          return byNumberNullsLast(scoreOf(a.email), scoreOf(b.email), true);
        }
        default:
          return 0;
      }
    });
  }, [subs, search, statusFilter, sourceFilter, duplicatesOnly, sortMode, engagement, duplicateEmails]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, sourceFilter, duplicatesOnly, sortMode, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const pageEnd = Math.min(filtered.length, pageStart + pageSize);
  const pagedSubscribers = filtered.slice(pageStart, pageEnd);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  const dueFollowUps = useMemo(() => {
    const now = Date.now();
    const soon = now + 24 * 60 * 60 * 1000;
    return Object.entries(followUps)
      .filter(([, f]) => f.at != null && f.at <= soon)
      .map(([email, f]) => ({ email, at: f.at as number, note: f.note, overdue: (f.at as number) <= now }))
      .sort((a, b) => a.at - b.at);
  }, [followUps]);

  if (error) return <div className="error">Failed to load: {error}</div>;
  if (!subs) return <div className="muted">Loading subscribers…</div>;

  return (
    <>
      {dueFollowUps.length > 0 && (
        <div className="card" style={{ marginBottom: 16, borderColor: 'var(--danger)' }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>
            Follow-ups due ({dueFollowUps.length})
          </h3>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {dueFollowUps.map((f) => (
              <li key={f.email} className="row" style={{ gap: 10, padding: '4px 0', fontSize: 13, flexWrap: 'wrap' }}>
                <span className={`badge ${f.overdue ? 'unsubscribed' : ''}`} style={{ fontSize: 10 }}>
                  {f.overdue ? 'overdue' : 'today'}
                </span>
                <Link to={`/subscriber/${encodeURIComponent(f.email)}`}>{f.email}</Link>
                <span className="muted">{new Date(f.at).toLocaleString()}</span>
                {f.note && <span>— {f.note}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="row" style={{ gap: 32, justifyContent: 'space-between' }}>
          <div className="row" style={{ gap: 32 }}>
          <Stat label="Total rows" value={counts.total} />
          <Stat label="Unique emails" value={counts.unique} />
          <Stat label="Subscribed" value={counts.active} />
          <Stat label="Unsubscribed" value={counts.unsubscribed} />
          </div>
          {isStaleView && (
            <span className="muted" style={{ fontSize: 12 }}>
              Showing cached data, refreshing...
            </span>
          )}
          {!isStaleView && lastSyncedAt && (
            <span className="muted" style={{ fontSize: 12 }}>
              Last synced {formatRelativeSync(lastSyncedAt)}
            </span>
          )}
        </div>
      </div>

      <div className="card">
        <div className="field" style={{ marginBottom: 12 }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, or tag…"
            aria-label="Search subscribers"
          />
        </div>

        <div className="row" style={{ marginBottom: 12, gap: 12 }}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            style={{
              background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)',
              borderRadius: 8, padding: '10px 12px', font: 'inherit',
            }}
          >
            <option value="all">All statuses</option>
            <option value="subscribed">Subscribed only</option>
            <option value="unsubscribed">Unsubscribed only</option>
          </select>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            style={{
              background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)',
              borderRadius: 8, padding: '10px 12px', font: 'inherit',
            }}
          >
            <option value="all">All sources</option>
            {availableSources.map((source) => (
              <option key={source} value={source}>{source}</option>
            ))}
          </select>
          <select
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value as SortMode)}
            style={{
              background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)',
              borderRadius: 8, padding: '10px 12px', font: 'inherit',
            }}
          >
            <option value="last-joined-desc">Sort: last joined (newest first)</option>
            <option value="last-joined-asc">Sort: last joined (oldest first)</option>
            <option value="name-asc">Sort: name (A → Z)</option>
            <option value="name-desc">Sort: name (Z → A)</option>
            <option value="email-asc">Sort: email (A → Z)</option>
            <option value="tag-asc">Sort: tag (A → Z)</option>
            <option value="last-event-desc">Sort: last event (newest first)</option>
            <option value="last-event-asc">Sort: last event (oldest first)</option>
            <option value="engaged-desc">Sort: most engaged (clicks + opens)</option>
          </select>
        </div>

        <div className="row" style={{ marginBottom: 16, gap: 10, justifyContent: 'space-between' }}>
          <label style={{ margin: 0, textTransform: 'none', letterSpacing: 0, fontSize: 12 }}>
            <input
              type="checkbox"
              checked={duplicatesOnly}
              onChange={(e) => setDuplicatesOnly(e.target.checked)}
              style={{ marginRight: 6 }}
            />
            Duplicates only ({duplicateEmails.size})
          </label>
          <div className="row" style={{ gap: 8 }}>
            <button
              type="button"
              className="primary subscribers-page-btn"
              onClick={() => setShowAddModal(true)}
            >
              + Add subscriber
            </button>
            <button
              type="button"
              className="secondary subscribers-page-btn"
              onClick={() => setShowAdvanced((v) => !v)}
            >
              {showAdvanced ? 'Hide advanced actions' : 'Show advanced actions'}
            </button>
          </div>
        </div>

        {showAdvanced && (
          <div
            style={{
              marginBottom: 16,
              padding: 12,
              border: '1px solid var(--border)',
              borderRadius: 10,
              background: 'var(--bg)',
            }}
          >
            <div className="muted" style={{ fontSize: 12, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Advanced actions
            </div>
            <div className="row" style={{ marginBottom: 12, gap: 8 }}>
              <input
                type="text"
                value={bulkTagDraft}
                onChange={(e) => setBulkTagDraft(e.target.value)}
                placeholder="Tag for selected subscribers"
                style={{ maxWidth: 260 }}
              />
              <button
                type="button"
                className="secondary subscribers-page-btn"
                onClick={() => applyBulkTag('add')}
                disabled={bulkBusy || selectedEmails.size === 0 || !bulkTagDraft.trim()}
              >
                Add tag ({selectedEmails.size})
              </button>
              <button
                type="button"
                className="secondary subscribers-page-btn"
                onClick={() => applyBulkTag('remove')}
                disabled={bulkBusy || selectedEmails.size === 0 || !bulkTagDraft.trim()}
              >
                Remove tag ({selectedEmails.size})
              </button>
              <button type="button" className="secondary subscribers-page-btn" onClick={exportCsv}>
                Export CSV
              </button>
            </div>

            <div className="row" style={{ gap: 8 }}>
              <input
                type="text"
                value={segmentName}
                onChange={(e) => setSegmentName(e.target.value)}
                placeholder="Save current view as segment..."
                style={{ maxWidth: 320 }}
              />
              <button
                type="button"
                className="secondary subscribers-page-btn"
                onClick={saveCurrentViewAsSegment}
                disabled={segmentBusy}
              >
                {segmentBusy ? 'Saving…' : 'Save view as segment'}
              </button>
              {segmentMessage && <span className="muted" style={{ fontSize: 12 }}>{segmentMessage}</span>}
            </div>
          </div>
        )}

        {filtered.length === 0 ? (
          <p className="muted" style={{ textAlign: 'center', padding: 24 }}>
            No subscribers match.
          </p>
        ) : (
          <>
            <div className="row subscribers-pagination-row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
              <div className="muted" style={{ fontSize: 12 }}>
                Showing {pageStart + 1}-{pageEnd} of {filtered.length}
              </div>
              <div className="row" style={{ gap: 8 }}>
                <label
                  htmlFor="subscriber-page-size"
                  className="muted"
                  style={{ margin: 0, textTransform: 'none', letterSpacing: 0, fontSize: 12 }}
                >
                  Per page
                </label>
                <select
                  id="subscriber-page-size"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  style={{
                    background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)',
                    borderRadius: 8, padding: '6px 10px', font: 'inherit',
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>

            <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Tag</th>
                  <th>Status</th>
                  <th>Score</th>
                  <th>Last event</th>
                  <th>Follow-up</th>
                  <th>Joined</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pagedSubscribers.map((s) => {
                  const eng = engagement[s.email];
                  return (
                    <tr key={`${s.row}-${s.email}`}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedEmails.has(s.email)}
                          onChange={() => toggleSelected(s.email)}
                          aria-label={`Select ${s.email}`}
                        />
                      </td>
                      <td>{s.name || <span className="muted">—</span>}</td>
                      <td>
                        <Link to={`/subscriber/${encodeURIComponent(s.email)}`}>{s.email}</Link>
                      </td>
                      <td>{s.source || <span className="muted">—</span>}</td>
                      <td><span className={`badge ${s.status}`}>{s.status}</span></td>
                      <td>
                        {eng ? (
                          <span
                            className={`badge ${eng.score === 'hot' ? 'subscribed' : eng.score === 'warm' ? '' : 'unsubscribed'}`}
                            title={`${eng.opens} opens, ${eng.clicks} clicks (last 90 days)`}
                          >
                            {eng.score}
                          </span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="muted">{eng?.lastOpenAt ? new Date(eng.lastOpenAt).toLocaleString() : '—'}</td>
                      <td>
                        {followUps[s.email.toLowerCase()]?.at ? (
                          <span
                            className={`badge ${Number(followUps[s.email.toLowerCase()].at) <= Date.now() ? 'unsubscribed' : ''}`}
                            title={followUps[s.email.toLowerCase()].note || ''}
                          >
                            {Number(followUps[s.email.toLowerCase()].at) <= Date.now() ? 'Due' : 'Set'}
                          </span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="muted">{formatIsraeliTimestamp(s.date)}</td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button
                          className="secondary"
                          style={{ padding: '4px 12px', fontSize: 12 }}
                          onClick={() => toggleStatus(s)}
                          disabled={busyEmail === s.email}
                        >
                          {busyEmail === s.email
                            ? '…'
                            : s.status === 'subscribed'
                              ? 'Unsubscribe'
                              : 'Resubscribe'}
                        </button>
                        <button
                          className="secondary"
                          title="Permanently remove this entry"
                          style={{ padding: '4px 10px', fontSize: 12, marginLeft: 6, color: 'var(--danger)' }}
                          onClick={() => { setDeleteError(null); setDeleteTarget(s); }}
                          disabled={busyEmail === s.email}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>

            <div className="row subscribers-pagination-row" style={{ justifyContent: 'space-between', marginTop: 12 }}>
              <div className="muted" style={{ fontSize: 12 }}>
                Page {safePage} of {totalPages}
              </div>
              <div className="row" style={{ gap: 8 }}>
                <button
                  type="button"
                  className="secondary subscribers-page-btn"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="secondary subscribers-page-btn"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {deleteTarget && (
        <div
          onClick={() => !deleteBusy && setDeleteTarget(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 100,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            className="card"
            onClick={(e) => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 440, borderColor: 'var(--danger)' }}
          >
            <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Remove subscriber?</h3>
            <p style={{ margin: '0 0 8px', fontSize: 14 }}>
              This permanently deletes <strong>{deleteTarget.name || deleteTarget.email}</strong>{' '}
              (<code>{deleteTarget.email}</code>) from the Google Sheet — including their tags and notes.
              This cannot be undone.
            </p>
            <p className="muted" style={{ margin: '0 0 16px', fontSize: 13 }}>
              If they asked to stop receiving emails, use <strong>Unsubscribe</strong> instead — that keeps
              the record so they are never accidentally re-added.
            </p>
            {deleteError && <div className="error" style={{ marginBottom: 12 }}>Remove failed: {deleteError}</div>}
            <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="secondary" onClick={() => setDeleteTarget(null)} disabled={deleteBusy}>
                Cancel
              </button>
              <button
                type="button"
                className="primary"
                style={{ background: 'var(--danger)' }}
                onClick={confirmDelete}
                disabled={deleteBusy}
              >
                {deleteBusy ? 'Removing…' : 'Remove permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <AddSubscriberModal
          knownSources={availableSources}
          onClose={() => setShowAddModal(false)}
          onAdded={() => {
            setShowAddModal(false);
            refresh();
          }}
        />
      )}
    </>
  );
}

function AddSubscriberModal({
  knownSources,
  onClose,
  onAdded,
}: {
  knownSources: string[];
  onClose: () => void;
  onAdded: () => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [source, setSource] = useState('manual');
  const [tagsInput, setTagsInput] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail.includes('@')) { setError('Enter a valid email.'); return; }
    setBusy(true);
    setError(null);
    try {
      await api.addSubscriber({
        name: name.trim(),
        email: cleanEmail,
        source: source.trim() || 'manual',
        tags: tagsInput.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean),
        notes: notes.trim(),
      });
      onAdded();
    } catch (e) {
      const msg = (e as Error).message;
      setError(msg === 'duplicate_email' ? 'This email is already in the list.' : `Add failed: ${msg}`);
      setBusy(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        className="card"
        onClick={(e) => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' }}
      >
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>Add subscriber</h3>
          <button type="button" className="secondary" onClick={onClose} disabled={busy} style={{ padding: '4px 10px' }}>
            ×
          </button>
        </div>

        {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}

        <div className="field">
          <label>Name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" disabled={busy} />
        </div>
        <div className="field">
          <label>Email *</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
            placeholder="someone@example.com"
            disabled={busy}
          />
        </div>
        <div className="field">
          <label>Source</label>
          <input
            type="text"
            list="add-subscriber-sources"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            placeholder="manual"
            disabled={busy}
          />
          <datalist id="add-subscriber-sources">
            {knownSources.map((s) => <option key={s} value={s} />)}
          </datalist>
        </div>
        <div className="field">
          <label>Tags (comma-separated, optional)</label>
          <input type="text" value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="vip, purchased" disabled={busy} />
        </div>
        <div className="field">
          <label>Notes (optional)</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} disabled={busy} style={{ width: '100%', resize: 'vertical' }} />
        </div>
        <p className="muted" style={{ fontSize: 12, margin: '0 0 12px' }}>
          Joined date is stamped automatically (Israeli DD/MM/YYYY) and status starts as subscribed.
        </p>
        <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="primary" onClick={submit} disabled={busy || !email.trim()}>
            {busy ? 'Adding…' : 'Add subscriber'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function readSubscribersCache(): SubscribersCachePayload | null {
  try {
    const raw = localStorage.getItem(SUBSCRIBERS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SubscribersCachePayload;
    if (!parsed || !Array.isArray(parsed.subscribers) || typeof parsed.cachedAt !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeSubscribersCache(subscribers: Subscriber[]): void {
  try {
    const payload: SubscribersCachePayload = { subscribers, cachedAt: Date.now() };
    localStorage.setItem(SUBSCRIBERS_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // Best-effort cache only.
  }
}

function formatRelativeSync(timestamp: number): string {
  const deltaMs = Math.max(0, Date.now() - timestamp);
  const sec = Math.floor(deltaMs / 1000);
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  return `${hrs}h ago`;
}
